import { createFileRoute } from '@tanstack/react-router';
import { useStore } from '@tanstack/react-store';
import * as React from 'react';
import { gallery } from '~/data/gallery';
import { cn } from '~/lib/utils';
import { PageSection } from '~/routes/__root';
import { langStore } from '~/store/langStore';

export const Route = createFileRoute('/gallery')({
  component: RouteComponent,
});

function RouteComponent() {
  const [selectedImage, setSelectedImage] = React.useState<string | null>(null);
  const [selectedYear, setSelectedYear] = React.useState<string>(() => {
    // Set default to the highest year
    return Math.max(...gallery.map(item => Number.parseInt(item.year))).toString();
  });

  const locale = useStore(langStore, s => s);
  const dayTexts = {
    HU: 'nap',
    EN: 'day',
    RS: 'dan',
  };

  // Prevent body scroll when lightbox is open
  React.useEffect(() => {
    if (selectedImage) {
      document.body.style.overflow = 'hidden';
    }
    else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedImage]);

  const filteredGallery = React.useMemo(() => {
    return gallery.filter(item => item.year === selectedYear);
  }, [selectedYear]);

  return (
    <>
      <PageSection className={cn(selectedImage ? 'opacity-95' : 'opacity-100')}>
        {/* Year tabs */}
        <div className="mb-12 flex justify-center gap-3">
          {gallery.map(item => (
            <button
              type="button"
              key={item.year}
              data-umami-event="gallery-button"
              data-umami-event-year={item.year}
              onClick={() => setSelectedYear(item.year)}
              className={cn(
                'rounded-full px-6 py-2.5 font-medium transition-all duration-200',
                selectedYear === item.year
                  ? 'bg-red shadow-red/20 text-white shadow-lg'
                  : 'text-red bg-white/5 hover:text-red/80 hover:bg-white/10',
              )}
            >
              {item.year}
            </button>
          ))}
        </div>

        {filteredGallery.map(item => (
          <div key={item.year} className="mb-12">
            {/* <h1 className="text-3xl font-bold mb-8 text-center">
              {item.year}
            </h1> */}
            {item.days.map(day => (
              <div key={day.day} className="mb-10">
                <h2 className="mb-6 border-b pb-2 text-xl font-semibold">
                  {day.day}
                  .
                  {' '}
                  {dayTexts[locale]}
                </h2>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {day.images.map((image, index) => (
                    <div
                      key={index}
                      className="group aspect-square cursor-pointer overflow-hidden rounded-lg shadow-md"
                      data-umami-event="open-img"
                      data-umami-event-image={image}
                      onClick={() =>
                        setSelectedImage(
                          `https://assets.malomfesztival.org/${image}`,
                        )}
                    >
                      <img
                        src={`https://assets.malomfesztival.org/${image}`}
                        alt={`Gallery image ${index + 1} from day ${day.day} of ${item.year} festival`}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}

        {/* Lightbox */}
        {selectedImage && (
          <div
            className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4 py-20"
            onClick={() => setSelectedImage(null)}
          >
            {/* Background overlay - positioned behind the navbar */}
            {/* <div className="absolute inset-0 bg-black bg-opacity-30 -z-10" /> */}

            {/* Image container */}
            <div className="object-fit relative max-h-full max-w-full">
              <img
                src={selectedImage}
                alt="Gallery image"
                className="max-h-screen max-w-full rounded-lg object-scale-down shadow-2xl"
                onClick={e => e.stopPropagation()}
              />
              <button
                type="button"

                className="bg-opacity-50 absolute top-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-black text-3xl text-white transition-colors hover:text-gray-300"
                onClick={() => setSelectedImage(null)}
              >
                ×
              </button>
            </div>
          </div>
        )}
      </PageSection>
    </>
  );
}

export default RouteComponent;
